// # 동기화 엔진 — 클라이언트에서 가장 틀리기 쉬운 곳
//
// 메신저 클라이언트의 버그는 대부분 여기 모인다. "메시지가 안 와요", "읽음이
// 안 맞아요", "새로고침하면 사라져요" 는 전부 같은 뿌리다 — **연결이 끊긴 동안
// 무슨 일이 있었는지 모른다는 것.**
//
// ## 이 엔진의 단 하나의 규칙
//
// > 소켓은 **알림**이고, REST 가 **진실**이다.
//
// 소켓 사건은 "채널 42 의 seq 1337 이 생겼다" 만 나른다. 본문은 없다.
// 클라이언트는 번호가 이어지는지 보고, 안 이어지면 REST 로 메운다.
//
// 이 구조의 대가는 메시지 한 건당 왕복이 하나 더 는다는 것이고, 얻는 것은
// **어떤 순서로 무엇을 잃어도 결국 같은 상태로 수렴한다**는 성질이다.
// 모바일에서 지하철을 타 보면 이게 왜 값진지 안다.

import {
  BACKFILL_CHUNK,
  backfill_plan,
  type ChannelCursor,
  type ChannelId,
  DEFAULT_HEARTBEAT_SECS,
  type Event,
  type Inbound,
  is_new_line,
  type Outbound,
  PROTO_VERSION,
  type Seq,
  seq,
  verdict,
} from '@maru/protocol'

export type Connecting = 'Dropped' | 'Connecting' | 'Attached' | 'catching_up'

export interface SyncStatus {
  conn: Connecting
  /** 채널별로 **연속으로 가진** 마지막 번호. 이 값이 커서다. */
  cursor: Map<ChannelId, Seq>
}

export interface EngineConfig {
  socket_url: string
  /**
   * **한 번 쓰고 버리는 소켓 입장권**을 가져온다. 액세스 토큰이 아니다.
   *
   * 구현은 `POST /v1/socket-ticket` 을 `Authorization: Bearer` 로 부르고
   * 받은 `ticket` 을 돌려주면 된다. 재연결 때마다 새로 받아야 한다 —
   * 수명이 30초이고 한 번 쓰면 서버에서 지워진다.
   */
  ticket(): Promise<string>
  /** 갭을 메운다. 구현은 REST 호출이다. */
  backfill(channel: ChannelId, since: Seq, upto: Seq): Promise<void>
  /**
   * 이 채널들의 사건을 놓쳤을 수 있다. 다시 붙은 뒤와 서버가 재동기화를 요구했을 때
   * 부른다. 새 줄은 엔진이 갭으로 메우지만, 수정·삭제·반응은 번호가 늘지 않아
   * 갭으로 드러나지 않는다 — 화면이 가진 것을 다시 받아야 한다.
   */
  stale?(channels: readonly ChannelId[]): void
  /** 사건이 적용 가능할 때 UI 에 알린다. */
  apply(event: Event): void
  changed(status: SyncStatus): void
}

/** 재연결 간격. 지수적으로 늘리되 상한을 둔다. */
const reconnect_ms = [500, 1000, 2000, 5000, 10_000, 30_000] as const

/** 한 번에 메우는 최대 덩이 수. 넘치는 갭은 최신 쪽만 가져온다. */
const MAX_CHUNKS = 5

export class SyncEngine {
  #config: EngineConfig
  #socket: WebSocket | null = null
  #cursor = new Map<ChannelId, Seq>()
  #conn: Connecting = 'Dropped'
  #tries = 0
  #heartbeat_timer: ReturnType<typeof setInterval> | null = null
  /**
   * 붙기 세대. `start`·`stop` 마다 올린다. 티켓을 기다리는 사이 멈추거나 다시 시작하면 늦게 끝난
   * 붙기는 소켓을 열지 않고, 멈춘 뒤의 닫힘은 다시 붙지 않는다 — 안 그러면 주인 없는 소켓이 남아 같은 사건을
   * 두 번 적용한다(멘션 배지·반응 수가 두 배로 오른다, 3차 F128. StrictMode 의 start·stop·start 가 늘 그 모양이다).
   */
  #gen = 0
  /** 한 번이라도 붙었는가. 첫 연결은 막 받아 온 것이라 낡지 않았다. */
  #attached_before = false

  /**
   * 갭을 메우는 동안 들어온 사건을 쌓아 둔다.
   *
   * **이 버퍼가 없으면** 백필 응답과 실시간 사건이 경쟁해 커서가 뒤로 갔다 앞으로
   * 갔다 한다. 그 순간 안읽음 배지가 춤춘다.
   */
  #pending = new Map<ChannelId, Event[]>()

  constructor(config: EngineConfig) {
    this.#config = config
  }

  get status(): SyncStatus {
    return { conn: this.#conn, cursor: new Map(this.#cursor) }
  }

  /** 로컬에 이미 가진 것을 알려 준다. 앱 시작 시 한 번 부른다. */
  seed_cursor(cursor: Iterable<ChannelCursor>): void {
    for (const c of cursor) this.#cursor.set(c.channel, c.have)
  }

  /** `prep` 은 붙기 전에 할 일(커서 심기). 그 사이 멈추면 붙지 않는다. */
  async start(prep?: () => Promise<void>): Promise<void> {
    const gen = ++this.#gen
    // 받아 오기 동안도 붙는 중이다. 첫 상태(끊김)로 두면 여는 순간마다 "연결이 끊겼어요" 가 번쩍인다(3차 F319).
    this.#to_status('Connecting')
    await prep?.()
    if (gen === this.#gen) await this.#attach(gen)
  }

  stop(): void {
    this.#gen += 1
    this.#heartbeat_off()
    this.#socket?.close(1000)
    this.#socket = null
    this.#to_status('Dropped')
  }

  send(cmd: Inbound): boolean {
    if (this.#socket?.readyState !== WebSocket.OPEN) return false
    this.#socket.send(JSON.stringify(cmd))
    return true
  }

  async #attach(gen: number): Promise<void> {
    this.#to_status('Connecting')
    // 브라우저 WebSocket 은 헤더를 못 붙인다. 그래서 질의로 넘기는데,
    // **넘기는 것은 액세스 토큰이 아니라 한 번 쓰고 버리는 티켓이다.**
    // 질의 문자열은 프록시·CDN 접근 로그에 평문으로 남는다. 거기 남은 값이
    // 이미 소모된 30초짜리 난수라면 주워도 쓸 것이 없다.
    // 티켓을 못 받으면(서버가 막 다시 뜨는 중·망이 끊김) 소켓 끊김과 같이 물러났다가 다시 온다.
    // 던진 채 두면 다음 붙기가 예약되지 않아 "연결하는 중" 에 영영 머문다(3차 F240).
    let ticket: string
    try {
      ticket = await this.#config.ticket()
    } catch {
      if (gen !== this.#gen) return
      this.#to_status('Dropped')
      this.#reattach(gen)
      return
    }
    // 기다리는 사이 멈췄거나 다른 붙기가 시작됐다 — 이 붙기는 늦었다.
    if (gen !== this.#gen) return
    const socket = new WebSocket(`${this.#config.socket_url}?ticket=${encodeURIComponent(ticket)}`)
    this.#socket = socket

    socket.onopen = () => {
      this.#tries = 0
      this.#to_status('catching_up')
      // 붙자마자 **가진 것을 전부 말한다.** 서버가 갭을 계산해 돌려준다.
      const have: ChannelCursor[] = [...this.#cursor].map(([channel, have]) => ({ channel, have }))
      this.send({ op: 'resume', have })
      this.#heartbeat_on()
    }

    socket.onmessage = (e) => void this.#Got(e.data as string)

    socket.onclose = (e) => {
      this.#heartbeat_off()
      this.#to_status('Dropped')
      if (gen !== this.#gen) return
      // 1001(Going Away) 은 서버가 **정중하게** 보낸 것이다 — 배포 중 드레인.
      // 서버가 이미 연결들을 시간차로 흩어 놓았으므로 여기서 또 오래 기다릴 이유가 없다.
      // 백오프 단계도 되감는다. 장애가 아니라 배포다.
      if (e.code === 1001) this.#tries = 0
      this.#reattach(gen)
    }

    socket.onerror = () => socket.close()
  }

  #reattach(gen: number): void {
    const wait = reconnect_ms[Math.min(this.#tries, reconnect_ms.length - 1)] ?? 30_000
    this.#tries += 1
    // 지터를 섞는다. 서버가 재시작하면 수천 대가 **같은 순간에** 돌아와서
    // 방금 살아난 서버를 다시 죽인다.
    const jitter = Math.random() * wait * 0.3
    setTimeout(() => {
      if (gen === this.#gen) void this.#attach(gen)
    }, wait + jitter)
  }

  async #Got(text: string): Promise<void> {
    let got: Outbound
    try {
      got = JSON.parse(text) as Outbound
    } catch {
      return // 모르는 프레임은 무시한다. 서버가 앞서 있을 수 있다.
    }

    switch (got.op) {
      case 'greeting':
        if (got.proto !== PROTO_VERSION) {
          // 규약이 다르면 **추측하지 않는다.** 사용자에게 새로고침을 요구한다.
          console.error('proto mismatch', got.proto, PROTO_VERSION)
        }
        break

      case 'sync':
        await Promise.all(got.to_fill.map((gap) => this.#fill(gap.channel, gap.since, gap.upto)))
        if (this.#attached_before) this.#config.stale?.([...this.#cursor.keys()])
        this.#attached_before = true
        this.#to_status('Attached')
        break

      case 'event': {
        // 사건은 프레임에 펼쳐져 온다(`op` 옆에 `t`). `op` 만 떼어 사건으로 넘긴다.
        const { op: _, ...event } = got
        await this.#on_bus_event(event as Event)
        break
      }

      case 'needs_resync':
        // 서버가 사건을 흘렸다. 새 줄은 커서를 **그대로 두고** 다시 말해 갭으로 받는다 —
        // 커서를 지우면 그 채널이 `have` 에서 빠져 서버가 아무 갭도 계산하지 않는다.
        // 수정·반응은 갭으로 안 보이므로 화면에 다시 받으라고 알린다.
        this.#config.stale?.([got.channel])
        this.send({
          op: 'resume',
          have: [...this.#cursor].map(([channel, have]) => ({ channel, have })),
        })
        break

      case 'heartbeat_ack':
        break

      case 'fail':
        console.error('fail', got.reason) // 서버가 거절했다
        break
    }
  }

  async #on_bus_event(event: Event): Promise<void> {
    // 새 줄만 커서와 견준다. 수정·삭제·반응은 seq 가 있어도 **이미 있는 줄에 대한
    // 말**이라, 커서와 견주면 "이미 가졌다" 로 버려진다. 타이핑은 seq 자체가 없다.
    if (!is_new_line(event)) {
      this.#config.apply(event)
      return
    }

    const have = this.#cursor.get(event.channel) ?? seq(0)
    const result = verdict(have, event.seq)

    switch (result.kind) {
      case 'already_had':
        return

      case 'apply':
        this.#cursor.set(event.channel, event.seq)
        this.#config.apply(event)
        this.#config.changed(this.status)
        return

      case 'gap': {
        // 갭을 메우는 동안 온 사건은 쌓아 둔다. 메운 뒤에 순서대로 흘린다.
        const line = this.#pending.get(event.channel) ?? []
        line.push(event)
        this.#pending.set(event.channel, line)
        await this.#fill(event.channel, result.since, result.upto)
        return
      }
    }
  }

  async #fill(channel: ChannelId, since: Seq, upto: Seq): Promise<void> {
    if (upto < since) return

    for (const chunk of backfill_plan({ channel, since, upto }, MAX_CHUNKS)) {
      await this.#config.backfill(channel, chunk.since, chunk.upto)
      this.#cursor.set(channel, chunk.upto)
    }

    // 너무 큰 갭이라 앞을 버렸다면 커서가 `since` 앞에 남아 있다.
    // 그대로 두면 다음 사건마다 같은 갭을 다시 계산한다 — **앞은 포기했다고 기록한다.**
    const current = this.#cursor.get(channel) ?? seq(0)
    if (current < upto) this.#cursor.set(channel, upto)

    // 쌓아 둔 것을 순서대로 흘린다.
    const line = this.#pending.get(channel)
    if (line) {
      this.#pending.delete(channel)
      line.sort((a, b) => ('seq' in a && 'seq' in b ? a.seq - b.seq : 0))
      for (const e of line) await this.#on_bus_event(e)
    }

    this.#config.changed(this.status)
  }

  #heartbeat_on(): void {
    this.#heartbeat_off()
    this.#heartbeat_timer = setInterval(() => {
      if (!this.send({ op: 'heartbeat' })) this.#socket?.close()
    }, DEFAULT_HEARTBEAT_SECS * 1000)
  }

  #heartbeat_off(): void {
    if (this.#heartbeat_timer) clearInterval(this.#heartbeat_timer)
    this.#heartbeat_timer = null
  }

  #to_status(next: Connecting): void {
    if (this.#conn === next) return
    this.#conn = next
    this.#config.changed(this.status)
  }
}

/** 한 요청으로 메울 수 있는 최대치. 화면이 이보다 크면 두 번 부른다. */
export const max_fill_at_once = BACKFILL_CHUNK
